import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { formatMapName } from '../../liveSession/lib/formatters';
import { useMyTeam } from '../../team/useMyTeam';
import { formatStrategyType } from '../lib/formatPlaybook';
import {
  EMPTY_QUERY,
  filterPlaybook,
  isKnown,
  isQueryFiltered,
  listSearchString,
  mapOptions,
  parseListSearch,
  playbookStats,
  type PlaybookQuery,
  typeOptions,
} from '../lib/playbook';
import { listStrategies } from '../strategyApi';
import type { PlaybookSummary } from '../types';
import PlaybookShell from './PlaybookShell';
import styles from './StrategiesPage.module.css';

export default function StrategiesPage() {
  const { user } = useAuth();
  const { team, loading: teamLoading, error: teamError } = useMyTeam(user?.id);
  const [params, setParams] = useSearchParams();
  const query = parseListSearch(params);
  const [strategies, setStrategies] = useState<PlaybookSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!team?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listStrategies(team.id)
      .then((rows) => {
        if (!cancelled) setStrategies(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load strategies');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [team?.id, reloadToken]);

  const activeStrategies = useMemo(
    () => strategies.filter((strategy) => strategy.status.trim().toLowerCase() === 'active'),
    [strategies],
  );
  const stats = useMemo(() => playbookStats(activeStrategies), [activeStrategies]);
  const visible = useMemo(() => filterPlaybook(activeStrategies, query), [activeStrategies, query]);
  const maps = useMemo(() => mapOptions(activeStrategies), [activeStrategies]);
  const types = useMemo(() => typeOptions(activeStrategies), [activeStrategies]);
  const filtered = isQueryFiltered(query);
  const listQuery = listSearchString(query);

  function updateQuery(patch: Partial<PlaybookQuery>) {
    const next = { ...query, ...patch };
    setParams(listSearchString(next), { replace: true });
  }

  function resetFilters() {
    setParams(listSearchString({ ...EMPTY_QUERY, sort: query.sort }), { replace: true });
  }

  return (
    <PlaybookShell crumb="Strategies">
      <div className={styles.head}>
        <div>
          <div className={styles.eyebrow}>The team playbook</div>
          <h1>Strategies</h1>
          <p>Find the right call. Learn every role. Be ready for the next round.</p>
        </div>
        {team?.role === 'strategist' && (
          <Link className={styles.studio} to="/studio">Open studio</Link>
        )}
      </div>

      {teamLoading && <p className={styles.status}>Loading your team…</p>}
      {!teamLoading && teamError && <p className={styles.error}>{teamError.message}</p>}
      {!teamLoading && !teamError && !team && <p className={styles.status}>Join a team to see its playbook.</p>}

      {team && (
        <>
          <section className={styles.stats} aria-label="Your playbook progress">
            <div className={styles.stat}>
              <div>
                <div className={styles.micro}>Team strategies</div>
                <p>Across {stats.mapCount} maps</p>
              </div>
              <strong>{stats.total}</strong>
            </div>
            <div className={`${styles.stat} ${styles.knownStat}`}>
              <div>
                <div className={styles.micro}>Known to you</div>
                <p>Every role learned</p>
              </div>
              <strong>{stats.known}</strong>
            </div>
            <div className={`${styles.stat} ${styles.pendingStat}`}>
              <div>
                <div className={styles.micro}>Still to learn</div>
                <p>Your next practice starts here</p>
              </div>
              <strong>{stats.pending}</strong>
            </div>
          </section>

          <section className={styles.panel} aria-label="Strategy library">
            <div className={styles.filters}>
              <div className={styles.filterTop}>
                <label className={styles.search}>
                  <span className={styles.sr}>Search strategies</span>
                  <input
                    type="search"
                    value={query.q}
                    placeholder="Search by strategy, callout or route…"
                    onChange={(event) => updateQuery({ q: event.target.value })}
                  />
                </label>
                <div className={styles.segmented} role="group" aria-label="Your knowledge">
                  {([
                    ['all', 'All strategies'],
                    ['known', 'Known'],
                    ['pending', 'Not yet known'],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={query.knowledge === value}
                      onClick={() => updateQuery({ knowledge: value })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className={styles.filterBottom}>
                <label>
                  Map
                  <select value={query.map} onChange={(event) => updateQuery({ map: event.target.value })}>
                    <option value="all">All maps</option>
                    {maps.map((map) => (
                      <option key={map.value} value={map.value}>{map.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Type
                  <select value={query.type} onChange={(event) => updateQuery({ type: event.target.value })}>
                    <option value="all">All types</option>
                    {types.map((type) => (
                      <option key={type} value={type}>{formatStrategyType(type)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Side
                  <select
                    value={query.side}
                    onChange={(event) => updateQuery({ side: event.target.value as PlaybookQuery['side'] })}
                  >
                    <option value="all">Both sides</option>
                    <option value="T">T</option>
                    <option value="CT">CT</option>
                  </select>
                </label>
                <button type="button" className={styles.clear} disabled={!filtered} onClick={resetFilters}>
                  Reset filters
                </button>
              </div>
            </div>

            <div className={styles.results}>
              <span role="status">
                {loading ? 'Loading strategies…' : `${visible.length} of ${activeStrategies.length} strategies`}
              </span>
              <label className={styles.sort}>
                Sort by
                <select value={query.sort} onChange={(event) => updateQuery({ sort: event.target.value as PlaybookQuery['sort'] })}>
                  <option value="updated">Recently updated</option>
                  <option value="name">Name A–Z</option>
                  <option value="progress">Learning progress</option>
                </select>
              </label>
            </div>

            {error && (
              <div className={styles.empty}>
                <h2>Could not load strategies</h2>
                <p>{error}</p>
                <button type="button" onClick={() => setReloadToken((token) => token + 1)}>Retry</button>
              </div>
            )}

            {!error && !loading && activeStrategies.length === 0 && (
              <div className={styles.empty}>
                <div className={styles.eyebrow}>Empty playbook</div>
                <h2>No strategies yet</h2>
                <p>Strategies published for this team will show up here.</p>
              </div>
            )}

            {!error && !loading && activeStrategies.length > 0 && visible.length === 0 && (
              <div className={styles.empty}>
                <div className={styles.eyebrow}>No matching calls</div>
                <h2>No strategies found</h2>
                <p>Try a different search or broaden your filters.</p>
                <button type="button" onClick={resetFilters}>Reset all filters</button>
              </div>
            )}

            {!error && visible.length > 0 && (
              <div className={styles.tableWrap}>
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Strategy</th>
                      <th scope="col">Map / side</th>
                      <th scope="col">Type</th>
                      <th scope="col">Your knowledge</th>
                      <th scope="col"><span className={styles.sr}>Open</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((strategy) => {
                      const href = `/strategies/${encodeURIComponent(strategy.id)}${listQuery ? `?from=${encodeURIComponent(listQuery)}` : ''}`;
                      const known = isKnown(strategy);
                      return (
                        <tr key={strategy.id}>
                          <td>
                            <Link className={styles.name} to={href}>{strategy.name}</Link>
                            {strategy.description && <p className={styles.description}>{strategy.description}</p>}
                          </td>
                          <td>
                            <div className={styles.mapName}>{formatMapName(strategy.map) || strategy.map}</div>
                            <span className={`${styles.badge} ${styles[strategy.side.trim().toLowerCase()] ?? ''}`}>
                              {strategy.side.trim().toUpperCase()} side
                            </span>
                          </td>
                          <td>
                            {strategy.strategyType
                              ? <span className={styles.badge}>{formatStrategyType(strategy.strategyType)}</span>
                              : <span className={styles.muted}>—</span>}
                          </td>
                          <td>
                            <span className={`${styles.badge} ${known ? styles.known : styles.pending}`}>
                              {known ? 'Known' : strategy.rolesLearned > 0 ? 'Learning' : 'Not yet known'}
                            </span>
                            <div className={styles.learning}>
                              <span className={styles.ticks} aria-hidden="true">
                                {Array.from({ length: strategy.roleCount }, (_, index) => (
                                  <i key={index} className={index < strategy.rolesLearned ? styles.done : undefined} />
                                ))}
                              </span>
                              {strategy.rolesLearned}/{strategy.roleCount} roles
                            </div>
                          </td>
                          <td>
                            <Link className={styles.open} to={href} aria-label={`Open ${strategy.name}`}>↗</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className={styles.footer}>
              <span>Learning progress is personal to you.</span>
              <span>Known = every role learned</span>
            </div>
          </section>
        </>
      )}
    </PlaybookShell>
  );
}
